import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Save, Trash2, Download, Search, FileText, CheckCircle2, TrendingUp, Wallet, AlertCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { api, formatApiErrorDetail } from "../../lib/api";
import { invoicesApi } from "../../lib/copApi";
import { contactsApi } from "../../lib/contactsApi";
import { useAuthStore } from "../../store/authStore";

const STATUS_COLORS = {
  draft: "bg-zinc-100 text-zinc-700",
  sent: "bg-sky-100 text-sky-700",
  paid: "bg-emerald-100 text-emerald-700",
  overdue: "bg-red-100 text-red-700",
  void: "bg-zinc-200 text-zinc-500",
};
const STATUS_LABELS = {
  draft: "Taslak",
  sent: "Gönderildi",
  paid: "Ödendi",
  overdue: "Gecikmiş",
  void: "İptal",
};

function LineItemRow({ item, onChange, onRemove }) {
  const line = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
  const taxAmt = line * ((Number(item.tax_rate) || 0) / 100);
  return (
    <div className="grid grid-cols-12 items-center gap-2">
      <Input
        className="col-span-5"
        placeholder="Açıklama"
        value={item.description}
        onChange={(e) => onChange({ ...item, description: e.target.value })}
      />
      <Input
        className="col-span-2"
        type="number"
        placeholder="Adet"
        value={item.quantity}
        onChange={(e) => onChange({ ...item, quantity: e.target.value })}
      />
      <Input
        className="col-span-2"
        type="number"
        placeholder="Birim"
        value={item.unit_price}
        onChange={(e) => onChange({ ...item, unit_price: e.target.value })}
      />
      <Input
        className="col-span-1"
        type="number"
        placeholder="%"
        value={item.tax_rate}
        onChange={(e) => onChange({ ...item, tax_rate: e.target.value })}
      />
      <div className="col-span-1 text-right text-xs text-zinc-600">
        {(line + taxAmt).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="col-span-1 rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function InvoiceDialog({ open, onOpenChange, contacts, companies, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    number: "",
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    currency: "TRY",
    status: "draft",
    contact_id: "",
    company_id: "",
    items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: 20 }],
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          number: initial.number || "",
          issue_date: initial.issue_date || new Date().toISOString().slice(0, 10),
          due_date: initial.due_date || "",
          currency: initial.currency || "TRY",
          status: initial.status || "draft",
          contact_id: initial.contact_id || "",
          company_id: initial.company_id || "",
          items: (initial.items && initial.items.length > 0)
            ? initial.items.map((i) => ({ ...i }))
            : [{ description: "", quantity: 1, unit_price: 0, tax_rate: 20 }],
          notes: initial.notes || "",
        });
      } else {
        setForm({
          number: "",
          issue_date: new Date().toISOString().slice(0, 10),
          due_date: "",
          currency: "TRY",
          status: "draft",
          contact_id: "",
          company_id: "",
          items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: 20 }],
          notes: "",
        });
      }
    }
  }, [open, initial]);

  const totals = useMemo(() => {
    let subtotal = 0, tax = 0;
    for (const it of form.items) {
      const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
      subtotal += line;
      tax += line * ((Number(it.tax_rate) || 0) / 100);
    }
    return { subtotal, tax, total: subtotal + tax };
  }, [form.items]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        number: form.number || undefined, // let backend auto-generate
        contact_id: form.contact_id || null,
        company_id: form.company_id || null,
        due_date: form.due_date || null,
        items: form.items
          .filter((i) => (i.description || "").trim())
          .map((i) => ({
            description: i.description,
            quantity: Number(i.quantity) || 0,
            unit_price: Number(i.unit_price) || 0,
            tax_rate: Number(i.tax_rate) || 0,
          })),
      };
      const doc = isEdit
        ? await invoicesApi.update(initial.id, payload)
        : await invoicesApi.create(payload);
      toast.success(isEdit ? "Fatura güncellendi" : "Fatura oluşturuldu");
      onSaved(doc);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Faturayı düzenle" : "Yeni fatura"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Fatura no</Label>
              <Input
                value={form.number}
                onChange={(e) => setForm({ ...form, number: e.target.value })}
                placeholder="Otomatik"
              />
            </div>
            <div>
              <Label>Kesim tarihi</Label>
              <Input
                type="date"
                value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Vade</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kontak</Label>
              <Select
                value={form.contact_id || "__none__"}
                onValueChange={(v) => setForm({ ...form, contact_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {contacts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {[c.first_name, c.last_name].filter(Boolean).join(" ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Şirket</Label>
              <Select
                value={form.company_id || "__none__"}
                onValueChange={(v) => setForm({ ...form, company_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="mb-1 grid grid-cols-12 gap-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
              <div className="col-span-5">Açıklama</div>
              <div className="col-span-2">Adet</div>
              <div className="col-span-2">Birim fiyat</div>
              <div className="col-span-1">KDV %</div>
              <div className="col-span-1 text-right">Toplam</div>
              <div className="col-span-1"></div>
            </div>
            <div className="space-y-1">
              {form.items.map((it, idx) => (
                <LineItemRow
                  key={idx}
                  item={it}
                  onChange={(v) => {
                    const next = [...form.items];
                    next[idx] = v;
                    setForm({ ...form, items: next });
                  }}
                  onRemove={() => {
                    const next = form.items.filter((_, i) => i !== idx);
                    setForm({ ...form, items: next.length > 0 ? next : [{ description: "", quantity: 1, unit_price: 0, tax_rate: 20 }] });
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() =>
                setForm({
                  ...form,
                  items: [...form.items, { description: "", quantity: 1, unit_price: 0, tax_rate: 20 }],
                })
              }
              className="mt-2 inline-flex items-center gap-1 rounded-md border border-dashed border-zinc-300 px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-50"
            >
              <Plus className="h-3 w-3" /> Kalem ekle
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Notlar"
            />
            <div className="rounded-lg bg-zinc-50 p-3 text-sm">
              <div className="flex justify-between text-zinc-600">
                <span>Ara toplam</span>
                <span>{totals.subtotal.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-zinc-600">
                <span>KDV</span>
                <span>{totals.tax.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}</span>
              </div>
              <div className="mt-1 flex justify-between border-t border-zinc-200 pt-1 font-semibold text-zinc-900">
                <span>Genel toplam</span>
                <span>
                  {totals.total.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} {form.currency}
                </span>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button
            onClick={save}
            disabled={busy}
            className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
            data-testid="invoice-save-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CopInvoices() {
  const { user } = useAuthStore();
  const canManage =
    ["super_admin", "ultra_admin"].includes(user?.role) || user?.can_manage_contacts !== false;
  const [invoices, setInvoices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [inv, ct, co] = await Promise.all([
        invoicesApi.list(),
        contactsApi.list({ kind: "person" }),
        contactsApi.list({ kind: "company" }),
      ]);
      setInvoices(inv);
      setContacts(ct);
      setCompanies(co);
    } catch (err) {
      toast.error(err.message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = invoices;
    if (statusFilter !== "__all__") list = list.filter((x) => x.status === statusFilter);
    if (q.trim()) {
      const rx = q.trim().toLowerCase();
      list = list.filter(
        (x) =>
          (x.number || "").toLowerCase().includes(rx) ||
          (x.notes || "").toLowerCase().includes(rx),
      );
    }
    return list;
  }, [invoices, statusFilter, q]);

  const remove = async (inv) => {
    if (!window.confirm(`Sil: ${inv.number}?`)) return;
    try {
      await invoicesApi.remove(inv.id);
      setInvoices((cur) => cur.filter((x) => x.id !== inv.id));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onSaved = (doc) => {
    setInvoices((cur) => {
      const exists = cur.find((x) => x.id === doc.id);
      return exists ? cur.map((x) => (x.id === doc.id ? doc : x)) : [doc, ...cur];
    });
    setEditing(null);
  };

  // KPI aggregates for the header strip. Uses `filtered` so it reflects the
  // currently-visible slice (respects search + status filter).
  const kpi = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    let totalAmount = 0, paidAmount = 0, outstandingAmount = 0, overdueCount = 0;
    for (const inv of invoices) {
      const amt = Number(inv.total) || 0;
      totalAmount += amt;
      if (inv.status === "paid") paidAmount += amt;
      else if (inv.status !== "void") outstandingAmount += amt;
      if (inv.status !== "paid" && inv.status !== "void" && inv.due_date && inv.due_date < today) {
        overdueCount += 1;
      }
    }
    return {
      count: invoices.length,
      totalAmount,
      paidAmount,
      outstandingAmount,
      overdueCount,
    };
  }, [invoices]);

  const markPaid = async (inv) => {
    try {
      const updated = await invoicesApi.update(inv.id, { status: "paid" });
      toast.success(`${inv.number} ödendi olarak işaretlendi`);
      onSaved(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const csv = async () => {
    try {
      const res = await api.get("/invoices/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "invoices.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="cop-invoices-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">COP 31</div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            Faturalar
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Faturalarını oluştur, durumlarını takip et, CSV&apos;ye aktar.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-56">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Fatura ara"
              className="pl-7"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="invoice-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tüm durumlar</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={csv} className="gap-1 border-zinc-300">
            <Download className="h-3.5 w-3.5" />
            CSV
          </Button>
          {canManage && (
            <Button
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
              className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="invoice-add-btn"
            >
              <Plus className="h-4 w-4" />
              Yeni fatura
            </Button>
          )}
        </div>
      </div>

      {/* KPI strip — always visible above the table so status is at a glance. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="invoice-kpi-strip">
        <div className="rounded-xl border border-zinc-200 bg-white p-4">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            <FileText className="h-3.5 w-3.5" /> Toplam
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-zinc-900">
            {kpi.totalAmount.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}
          </div>
          <div className="mt-0.5 text-[11px] text-zinc-500">{kpi.count} fatura</div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-800">
            <CheckCircle2 className="h-3.5 w-3.5" /> Ödendi
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-emerald-900">
            {kpi.paidAmount.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}
          </div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-amber-800">
            <Wallet className="h-3.5 w-3.5" /> Bekleyen
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-amber-900">
            {kpi.outstandingAmount.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}
          </div>
        </div>
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-rose-800">
            <AlertCircle className="h-3.5 w-3.5" /> Gecikmiş
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-rose-900">
            {kpi.overdueCount}
          </div>
          <div className="mt-0.5 text-[11px] text-rose-700">vadesi geçmiş</div>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
        {filtered.length === 0 ? (
          <div className="px-6 py-14 text-center text-sm text-zinc-500">
            <FileText className="mx-auto mb-2 h-8 w-8 text-zinc-400" />
            Henüz fatura yok.
          </div>
        ) : (
          <>
            {/* ── Desktop table ── */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
              <thead className="border-b border-zinc-100 bg-zinc-50/60 text-[11px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Numara</th>
                  <th className="px-4 py-3 text-left font-semibold">Durum</th>
                  <th className="px-4 py-3 text-left font-semibold">Kesim</th>
                  <th className="px-4 py-3 text-left font-semibold">Vade</th>
                  <th className="px-4 py-3 text-left font-semibold">Kontak / Şirket</th>
                  <th className="px-4 py-3 text-right font-semibold">Toplam</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.map((inv) => {
                  const con = contacts.find((c) => c.id === inv.contact_id);
                  const co = companies.find((c) => c.id === inv.company_id);
                  return (
                    <tr
                      key={inv.id}
                      onClick={() => {
                        setEditing(inv);
                        setDialogOpen(true);
                      }}
                      className="cursor-pointer hover:bg-zinc-50"
                      data-testid={`invoice-row-${inv.id}`}
                    >
                      <td className="px-4 py-3 font-mono text-xs">{inv.number}</td>
                      <td className="px-4 py-3">
                        <span
                          className={
                            "inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium " +
                            (STATUS_COLORS[inv.status] || STATUS_COLORS.draft)
                          }
                        >
                          {STATUS_LABELS[inv.status] || inv.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-600">{inv.issue_date || "—"}</td>
                      <td className="px-4 py-3 text-zinc-600">{inv.due_date || "—"}</td>
                      <td className="px-4 py-3 text-zinc-600">
                        {con
                          ? [con.first_name, con.last_name].filter(Boolean).join(" ")
                          : co?.name || "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-zinc-900">
                        {(inv.total || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} {inv.currency}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canManage && inv.status !== "paid" && inv.status !== "void" && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                markPaid(inv);
                              }}
                              data-testid={`invoice-mark-paid-${inv.id}`}
                              title="Ödendi olarak işaretle"
                              className="rounded p-1 text-emerald-500 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {canManage && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                remove(inv);
                              }}
                              className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {/* ── Mobile card list ── */}
            <ul className="divide-y divide-zinc-100 md:hidden">
              {filtered.map((inv) => {
                const con = contacts.find((c) => c.id === inv.contact_id);
                const co = companies.find((c) => c.id === inv.company_id);
                const who = con
                  ? [con.first_name, con.last_name].filter(Boolean).join(" ")
                  : co?.name || "—";
                return (
                  <li
                    key={inv.id}
                    onClick={() => {
                      setEditing(inv);
                      setDialogOpen(true);
                    }}
                    data-testid={`invoice-row-${inv.id}`}
                    className="flex cursor-pointer flex-col gap-1.5 px-4 py-3 transition-colors active:bg-zinc-50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-zinc-700">
                            {inv.number}
                          </span>
                          <span
                            className={
                              "inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium " +
                              (STATUS_COLORS[inv.status] || STATUS_COLORS.draft)
                            }
                          >
                            {STATUS_LABELS[inv.status] || inv.status}
                          </span>
                        </div>
                        <div className="mt-0.5 truncate text-[13px] text-zinc-900">
                          {who}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-sm font-semibold text-zinc-900">
                          {(inv.total || 0).toLocaleString("tr-TR", {
                            maximumFractionDigits: 2,
                          })}{" "}
                          {inv.currency}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-zinc-500">
                      <span>
                        {inv.issue_date || "—"}
                        {inv.due_date ? ` · Vade: ${inv.due_date}` : ""}
                      </span>
                      <div
                        className="flex items-center gap-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {canManage &&
                          inv.status !== "paid" &&
                          inv.status !== "void" && (
                            <button
                              type="button"
                              onClick={() => markPaid(inv)}
                              data-testid={`invoice-mark-paid-${inv.id}`}
                              title="Ödendi olarak işaretle"
                              className="rounded p-1 text-emerald-500 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </button>
                          )}
                        {canManage && (
                          <button
                            type="button"
                            onClick={() => remove(inv)}
                            className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      <InvoiceDialog
        open={dialogOpen}
        onOpenChange={(v) => {
          setDialogOpen(v);
          if (!v) setEditing(null);
        }}
        contacts={contacts}
        companies={companies}
        initial={editing}
        onSaved={onSaved}
      />
    </div>
  );
}
